/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import {
  Cloud,
  CloudRain,
  Sun,
  CloudSnow,
  CloudDrizzle,
  Wind,
  Star,
} from "lucide-react";
import * as c from "../constants";
import GlobeStatsPanel from "./statPanel";
// import DingMoneyPanel from "./DingMoneyPanel";
// import DingMoneyDots from "./DingMoneyDots";
import { getReviews, Review } from "../data/getReviews";

const MAX_REVIEWS = 3;

interface OverlaysProps {
  transactionCount: number;
  /** "globe" = scattered fixed panels; "flat" = compact bottom bar */
  variant?: "globe" | "flat";
}

/**
 * All the screen chrome that sits on top of the map (logo, weather, live clock,
 * reviews, stats, Ding Money rollout). Shared by the globe and flat-map views.
 */
const Overlays: React.FC<OverlaysProps> = ({
  transactionCount,
  variant = "globe",
}) => {
  const numbers = new URLSearchParams(window.location.search).get("hn");
  const isFlat = variant === "flat";

  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewToasts, setReviewToasts] = useState<
    Array<{ id: number; review: Review }>
  >([]);
  const reviewIndexRef = useRef(0);

  const [weather, setWeather] = useState<{
    temp: string;
    location: string;
    condition: string;
    icon: any;
    feelsLike: string;
  }>({
    temp: "9°C",
    location: c.WEATHER_LOCATION.label,
    condition: "Partly Cloudy",
    icon: Cloud,
    feelsLike: "10°C",
  });
  const [currentTime, setCurrentTime] = useState(new Date());

  const getTransactionsPerMinute = () => {
    if (!transactionCount) return "";
    const n = new Date();
    return (transactionCount / n.getHours() / 60).toFixed(2);
  };

  const getTransactionsPerSecond = () => {
    if (!transactionCount) return "";
    const n = new Date();
    return (transactionCount / n.getHours() / 3600).toFixed(2);
  };

  const getTodayWithMonthName = () => {
    const today = new Date();
    const options: any = { year: "numeric", month: "long", day: "numeric" };
    return today.toLocaleDateString("en-US", options);
  };

  const getWeatherIcon = (condition: string) => {
    const lowerCondition = condition.toLowerCase();
    if (lowerCondition.includes("rain") || lowerCondition.includes("shower"))
      return CloudRain;
    if (lowerCondition.includes("snow")) return CloudSnow;
    if (lowerCondition.includes("drizzle")) return CloudDrizzle;
    if (lowerCondition.includes("clear") || lowerCondition.includes("sunny"))
      return Sun;
    if (lowerCondition.includes("wind")) return Wind;
    return Cloud;
  };

  useEffect(() => {
    // Fetch weather data using Open-Meteo API (no API key required, CORS-friendly)
    const fetchWeather = async () => {
      try {
        const { latitude, longitude, timezone, label } = c.WEATHER_LOCATION;

        const res = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,apparent_temperature,weather_code&timezone=${encodeURIComponent(timezone)}`,
          { signal: AbortSignal.timeout(10000) },
        );

        if (!res.ok) {
          throw new Error(`Weather API returned ${res.status}`);
        }

        const data = await res.json();

        // Map WMO weather codes to conditions
        const weatherCode = data.current.weather_code;
        let condition = "Partly Cloudy";

        if (weatherCode === 0) condition = "Clear";
        else if (weatherCode <= 3) condition = "Partly Cloudy";
        else if (weatherCode <= 49) condition = "Foggy";
        else if (weatherCode <= 59) condition = "Drizzle";
        else if (weatherCode <= 69) condition = "Rain";
        else if (weatherCode <= 79) condition = "Snow";
        else if (weatherCode <= 84) condition = "Rain Showers";
        else if (weatherCode <= 99) condition = "Thunderstorm";

        setWeather({
          temp: `${Math.round(data.current.temperature_2m)}°C`,
          location: label,
          condition: condition,
          icon: getWeatherIcon(condition),
          feelsLike: `${Math.round(data.current.apparent_temperature)}°C`,
        });
      } catch (err) {
        console.error("Weather fetch error:", err);
      }
    };

    fetchWeather();

    // Fetch reviews
    getReviews()
      .then((reviewsData) => {
        const filteredReviews = reviewsData.filter((r) => r.rating >= 2);
        setReviews(filteredReviews);
      })
      .catch((err) => console.error("Reviews fetch error:", err));

    // Update time every second
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, []);

  // Separate effect for review timer that depends on reviews state
  useEffect(() => {
    if (reviews.length === 0) return;

    const removalTimeouts: ReturnType<typeof setTimeout>[] = [];

    const showNextReview = () => {
      const review = reviews[reviewIndexRef.current % reviews.length];
      const toastId = Date.now() + Math.random();
      reviewIndexRef.current++;

      setReviewToasts((prev) => {
        const next = [...prev, { id: toastId, review }].slice(-MAX_REVIEWS);

        // Shorten display time as the stack fills up so reviews keep cycling
        const dynamicTimeout = Math.max(8000, 15000 - next.length * 1200);
        const removal = setTimeout(() => {
          setReviewToasts((current) => current.filter((t) => t.id !== toastId));
        }, dynamicTimeout);
        removalTimeouts.push(removal);

        return next;
      });
    };

    // Show first review after 5 seconds, then one every 20 seconds
    const initialTimeout = setTimeout(showNextReview, 5000);
    const reviewTimer = setInterval(showNextReview, 20000);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(reviewTimer);
      removalTimeouts.forEach((t) => clearTimeout(t));
    };
  }, [reviews]);

  return (
    <>
      <div
        className="logo-wrap"
        style={isFlat ? { top: "0.75rem", left: "1rem" } : undefined}
      >
        <svg
          data-testid="site-logo"
          xmlns="http://www.w3.org/2000/svg"
          width={isFlat ? 120 : 210}
          height={isFlat ? 62 : 108}
          viewBox="0 0 70 36"
        >
          <g fill="none" fillRule="evenodd">
            <g fill={isFlat ? "#004A59" : "#ffffff"}>
              <g>
                <g>
                  <g>
                    <path
                      d="M177.946 37.027l4.4 4.4-1.494 1.494c.546 1.13.851 2.43.851 3.875v.003c0 2.136-1.137 4.237-3.102 5.649 3.137 1.79 3.344 4.202 3.344 5.132 0 5.51-4.792 7.577-9.651 7.577-6.101 0-9.57-3.2-9.225-8.4h5.91c-.08 3.119 2.236 3.41 4.208 3.222 1.232-.118 2.228-1.126 2.243-2.362.015-1.257-.735-2.31-3.067-2.31-5.722 0-9.479-3.307-9.479-8.577 0-5.29 4.13-8.65 9.336-8.71h.177c1.37.02 2.663.273 3.827.728l1.722-1.721zM132.29 33.11v22.4h-5.6l-.312-1.754c-1.447 1.653-3.693 2.238-5.589 2.135-4.997-.31-7.789-3.444-7.789-8.817 0-5.2 3.309-8.886 8.272-8.886 2.172 0 3.864.63 4.795 1.767v-6.845h6.223zm22.125 5.043h.235c3.585.035 6.574 1.93 6.574 7.991v9.367h-6.222v-9.333c0-2.1-1.248-2.824-2.42-2.824-1.516 0-2.87 1.034-2.87 3.272v8.885h-6.222v-16.8h5.6l.184 1.716c1.447-1.688 3.584-2.308 5.376-2.273zM141 38.71v16.8h-6.222v-16.8H141zm-18.281 4.61c-1.896 0-3.412 1.377-3.412 3.754s1.516 3.72 3.412 3.72c2.137 0 3.412-1.757 3.412-3.548 0-2.066-1.172-3.926-3.412-3.926zm49.643-.104c-1.861 0-3.31 1.24-3.31 3.513 0 2.033 1.449 3.341 3.31 3.341 1.689 0 3.205-1.067 3.205-3.34 0-1.93-1.344-3.514-3.205-3.514zM137.89 30c1.89 0 3.422 1.532 3.422 3.422 0 1.89-1.532 3.423-3.422 3.423-1.89 0-3.423-1.532-3.423-3.423 0-1.89 1.533-3.422 3.423-3.422z"
                      transform="translate(-113 -24548) translate(0 21561) translate(0 2832) translate(0 125)"
                    ></path>
                  </g>
                </g>
              </g>
            </g>
          </g>
        </svg>
      </div>
      {/* Globe layout: weather + clock stacked top-right */}
      {!isFlat && (
        <div
          style={{
            position: "fixed",
            top: "2rem",
            right: "2rem",
            zIndex: 50,
            display: "flex",
            flexDirection: "column",
            gap: "0.75rem",
          }}
        >
          <div
            style={{
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              borderRadius: "0.75rem",
              padding: "1rem",
              minWidth: "200px",
            }}
          >
            <div
              style={{
                color: "#004A59",
                fontSize: "0.875rem",
                fontWeight: "600",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                marginBottom: "0.5rem",
              }}
            >
              {weather.location}
            </div>
            <div
              style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}
            >
              <div
                style={{
                  color: "#1f2937",
                  fontSize: "2.1875rem",
                  fontWeight: "800",
                }}
              >
                {weather.temp}
              </div>
              <weather.icon size={40} color="#004A59" strokeWidth={2} />
            </div>
            <div
              style={{
                color: "#6b7280",
                fontSize: "0.9375rem",
                marginTop: "0.25rem",
              }}
            >
              Feels like {weather.feelsLike}
            </div>
          </div>
          <div
            style={{
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              borderRadius: "0.75rem",
              padding: "1rem",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div
              style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}
            >
              <div
                style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: "#ef4444",
                  animation: "pulseDot 2s infinite ease-in-out",
                }}
              ></div>
              <span
                style={{
                  color: "#004A59",
                  fontSize: "0.875rem",
                  fontWeight: "700",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                Live
              </span>
            </div>
            <span
              style={{
                color: "#004A59",
                fontSize: "1.5rem",
                fontWeight: "500",
              }}
            >
              {currentTime.toLocaleTimeString()}
            </span>
          </div>
        </div>
      )}
      <style>{`
        @keyframes pulseDot {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
            box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
          }
          50% {
            transform: scale(2);
            opacity: 0.8;
            box-shadow: 0 0 0 20px rgba(239, 68, 68, 0);
          }
        }
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(100%); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
      {!isFlat && numbers !== "true" && (
        <GlobeStatsPanel
          date={getTodayWithMonthName()}
          totalTopups={transactionCount}
          perMinute={getTransactionsPerMinute()}
          perSecond={getTransactionsPerSecond()}
        />
      )}
      {/* Reviews: top-right, flowing downward (higher up on the flat map where the weather card has moved to the bottom bar) */}
      <div
        style={{
          position: "fixed",
          right: "2rem",
          top: isFlat ? "2rem" : "13rem",
          zIndex: 50,
          display: "flex",
          flexDirection: "column",
          gap: "0.75rem",
          alignItems: "flex-end",
        }}
      >
        {reviewToasts.map(({ id, review }) => {
          const isPositive = review.rating >= 4;
          const accent = isPositive ? "#00B67A" : "#FF9800";
          const sourceLabel =
            review.source.charAt(0).toUpperCase() + review.source.slice(1);

          return (
            <div
              key={id}
              style={{
                animation: "slideInRight 0.25s ease-out",
                background: "#FFFFFF",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(0, 0, 0, 0.1)",
                borderLeft: `5px solid ${accent}`,
                borderRadius: "0.75rem",
                padding: "1rem",
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
                minWidth: "300px",
                maxWidth: "300px",
                width: "300px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  justifyContent: "space-between",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.25rem",
                  }}
                >
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      size={16}
                      fill={i < review.rating ? accent : "#dcdce6"}
                      color={i < review.rating ? accent : "#dcdce6"}
                      strokeWidth={0}
                    />
                  ))}
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.35rem",
                  }}
                >
                  <span
                    style={{
                      fontSize: "0.8rem",
                      fontWeight: 700,
                      color: accent,
                      textTransform: "uppercase",
                      letterSpacing: "0.03em",
                    }}
                  >
                    {sourceLabel}
                  </span>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 40 40"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect width="40" height="40" rx="4" fill={accent} />
                    <path
                      d="M20 8L23.09 17.26L32.82 17.26L25.01 23.02L28.09 32.28L20 26.52L11.91 32.28L14.99 23.02L7.18 17.26L16.91 17.26L20 8Z"
                      fill="white"
                    />
                  </svg>
                </div>
              </div>
              {review.title && (
                <div
                  style={{
                    fontSize: "1rem",
                    fontWeight: "700",
                    color: "#191919",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {review.title}
                </div>
              )}
              <div
                style={
                  {
                    fontSize: "0.9rem",
                    color: "#191919",
                    lineHeight: "1.45",
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitLineClamp: 4,
                    WebkitBoxOrient: "vertical",
                  } as any
                }
              >
                {review.content}
              </div>
              <div
                style={{
                  fontSize: "0.8rem",
                  color: "#707070",
                  fontWeight: "400",
                }}
              >
                {review.author}
              </div>
            </div>
          );
        })}
      </div>

      {/* Globe layout: Ding Money fixed bottom-left */}
      {/*  !isFlat && <DingMoneyPanel /> */}

      {/* Flat layout: one centered widget of equal-height segments along the bottom */}
      {isFlat && (
        <div
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: "1rem",
            zIndex: 50,
            display: "flex",
            justifyContent: "center",
            padding: "0 1rem",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              pointerEvents: "auto",
              display: "flex",
              alignItems: "stretch",
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              borderRadius: "0.9rem",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.18)",
              overflow: "hidden",
            }}
          >
            {/* Weather */}
            <div style={flatSegment}>
              <weather.icon size={30} color="#004A59" strokeWidth={2} />
              <div>
                <div
                  style={{
                    fontSize: "0.65rem",
                    color: "#004A59",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  {weather.location}
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: "0.4rem",
                  }}
                >
                  <span
                    style={{
                      fontSize: "1.4rem",
                      fontWeight: 800,
                      color: "#1f2937",
                      lineHeight: 1,
                    }}
                  >
                    {weather.temp}
                  </span>
                  <span style={{ fontSize: "0.65rem", color: "#6b7280" }}>
                    Feels {weather.feelsLike}
                  </span>
                </div>
              </div>
            </div>

            {/* Live clock */}
            <div style={{ ...flatSegment, ...flatDivider }}>
              <div
                style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: "#ef4444",
                  animation: "pulseDot 2s infinite ease-in-out",
                }}
              />
              <span
                style={{
                  color: "#004A59",
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                Live
              </span>
              <span
                style={{
                  color: "#004A59",
                  fontSize: "1.2rem",
                  fontWeight: 600,
                }}
              >
                {currentTime.toLocaleTimeString()}
              </span>
            </div>

            {/* Stats */}
            {numbers !== "true" && (
              <>
                <div
                  style={{
                    ...flatSegment,
                    ...flatDivider,
                    flexDirection: "column",
                    alignItems: "flex-start",
                    justifyContent: "center",
                    gap: "0.15rem",
                  }}
                >
                  <span
                    style={{
                      fontSize: "0.6rem",
                      color: "#004A59",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                    }}
                  >
                    Topups Today
                  </span>
                  <span
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: 800,
                      color: "#001273",
                      lineHeight: 1,
                    }}
                  >
                    {transactionCount.toLocaleString()}
                  </span>
                </div>
                <div
                  style={{
                    ...flatSegment,
                    ...flatDivider,
                    flexDirection: "column",
                    alignItems: "flex-start",
                    justifyContent: "center",
                    gap: "0.15rem",
                  }}
                >
                  <span
                    style={{
                      fontSize: "0.6rem",
                      color: "#004A59",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                    }}
                  >
                    Per Minute
                  </span>
                  <span
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: 800,
                      color: "#001273",
                      lineHeight: 1,
                    }}
                  >
                    {getTransactionsPerMinute()}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Flat layout: Ding Money rollout as a compact traffic-light widget, kept separate */}
      {/* isFlat && (
        <div
          style={{
            position: "fixed",
            left: "1rem",
            bottom: "1rem",
            zIndex: 50,
          }}
        >
          <DingMoneyDots />
        </div>
      ) */}
    </>
  );
};

const flatSegment: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.6rem",
  padding: "0.7rem 1.1rem",
};

const flatDivider: React.CSSProperties = {
  borderLeft: "1px solid rgba(0, 74, 89, 0.12)",
};

export default Overlays;
